import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Form, Row, Col, Alert, Button, Table } from "react-bootstrap";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const SCORING_METHODS = ["NONE", "SUM", "WEIGHTED_SUM", "AVERAGE", "PERCENTAGE"];

let bandIdCounter = 0;
function nextBandId() {
  bandIdCounter -= 1;
  return bandIdCounter;
}

// Phase 3A built this as configuration-only; Phase 3C is the phase where a
// real score actually gets calculated from a submitted response using
// exactly this configuration (services/formScoringService.js,
// services/formGradeService.js) — see PHASE_3C_SCORING_RULE_MATRIX.md for
// how scoring_method's 5 values (NONE/SUM/WEIGHTED_SUM/AVERAGE/PERCENTAGE)
// map onto "total points/weighted fields/weighted sections/percentage
// only/average" and how pass/fail is a separate result-determination step
// layered on top, not a 6th method. Published versions remain immutable —
// this editor is only reachable while status === "DRAFT" (readOnly otherwise).
const ScoringConfigEditor = forwardRef(function ScoringConfigEditor({ value, onChange, readOnly, error }, ref) {
  const gradeBandsSectionRef = useRef(null);
  const [highlightGradeBands, setHighlightGradeBands] = useState(false);

  // Exposed to FormBuilderPage so a clicked publish-readiness issue like
  // "Grade configuration is invalid" can scroll straight to this section
  // and draw the eye to it, instead of leaving the admin to find it inside
  // a whole tab of scoring settings.
  useImperativeHandle(ref, () => ({
    focusGradeBands: () => {
      gradeBandsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightGradeBands(true);
      setTimeout(() => setHighlightGradeBands(false), 2000);
    },
  }));

  const config = value || { scoring_enabled: false, scoring_method: "NONE", total_max_score: "", pass_percentage: "", grade_config_json: [] };
  // Defensive: the backend's grade-band validator requires an array (or
  // null) -- anything else is never a valid state for this editor to be
  // holding, so treat it the same as "no bands configured" rather than
  // throwing when spreading a non-array below.
  const bands = Array.isArray(config.grade_config_json) ? config.grade_config_json : [];

  const update = (patch) => onChange({ ...config, ...patch });

  // Score Preview -- pure arithmetic on values already sitting in `config`,
  // never a fabricated/sample calculated score. "Total Max Score" is
  // already labeled "(display only)" below, i.e. this editor has never
  // computed a real score from a submission -- that only happens per real
  // response, server-side (services/formScoringService.js). This block
  // just makes the two numbers an admin would otherwise have to do math on
  // themselves (pass threshold in points, given a max + a percentage)
  // visible while configuring.
  const totalMaxNum = Number(config.total_max_score);
  const passPctNum = Number(config.pass_percentage);
  const hasTotalMax = config.total_max_score !== "" && config.total_max_score != null && Number.isFinite(totalMaxNum);
  const hasPassPct = config.pass_percentage !== "" && config.pass_percentage != null && Number.isFinite(passPctNum);
  const passThreshold = hasTotalMax && hasPassPct ? Math.round(((passPctNum / 100) * totalMaxNum) * 100) / 100 : null;

  const addBand = () => {
    update({ grade_config_json: [...bands, { id: nextBandId(), minimum: "", maximum: "", grade: "", label: "" }] });
  };
  const updateBand = (index, patch) => {
    const next = [...bands];
    next[index] = { ...next[index], ...patch };
    update({ grade_config_json: next });
  };
  const removeBand = (index) => {
    update({ grade_config_json: bands.filter((_, i) => i !== index) });
  };

  return (
    <div className="scoring-config-editor">
      <Alert variant="secondary" className="small">
        Scores are calculated from real submitted responses using this configuration. Field-level scoring mode,
        weightage and NA-exclusion are set per field in the Structure tab. Weightages must total 100 (per section
        for Weighted Sections, or per section's fields for Weighted Fields) before this version can be published.
      </Alert>

      <Form.Check
        type="checkbox"
        label="Enable scoring for this version"
        checked={!!config.scoring_enabled}
        disabled={readOnly}
        onChange={(e) => update({ scoring_enabled: e.target.checked })}
        className="mb-3"
      />

      {config.scoring_enabled && (
        <>
          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Label>Scoring Method</Form.Label>
              <Form.Select disabled={readOnly} value={config.scoring_method} onChange={(e) => update({ scoring_method: e.target.value })}>
                {SCORING_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </Form.Select>
              <Form.Text muted>
                {config.scoring_method === "WEIGHTED_SUM" && "Uses section weightage if configured, otherwise falls back to per-field weightage within each section."}
                {config.scoring_method === "SUM" && "Sum of obtained points over sum of applicable maximum points."}
                {config.scoring_method === "AVERAGE" && "Unweighted mean of each applicable field's own percentage."}
                {config.scoring_method === "PERCENTAGE" && "Same calculation as Total Points; grade/result only shown if grade bands are configured below."}
              </Form.Text>
            </Col>
            <Col md={4}>
              <Form.Label>Total Max Score (display only)</Form.Label>
              <Form.Control type="number" disabled={readOnly} value={config.total_max_score ?? ""} onChange={(e) => update({ total_max_score: e.target.value })} />
            </Col>
            <Col md={4}>
              <Form.Label>Pass Percentage</Form.Label>
              <Form.Control type="number" min={0} max={100} disabled={readOnly} value={config.pass_percentage ?? ""} onChange={(e) => update({ pass_percentage: e.target.value })} />
              <Form.Text muted>Leave blank to skip PASS/FAIL determination entirely.</Form.Text>
            </Col>
          </Row>

          <div className="scoring-preview-box">
            <div className="form-props-group-label">Score Preview</div>
            <p className="scoring-preview-hint">
              Based on your current configuration. A real score is calculated per submission once responses come in.
            </p>
            <div className="scoring-preview-stats">
              <div className="scoring-preview-stat">
                <span className="scoring-preview-stat-label">Maximum Score</span>
                <span className="scoring-preview-stat-value">{hasTotalMax ? config.total_max_score : "—"}</span>
              </div>
              <div className="scoring-preview-stat">
                <span className="scoring-preview-stat-label">Pass Threshold</span>
                <span className="scoring-preview-stat-value">
                  {passThreshold != null ? `≥ ${passThreshold} / ${config.total_max_score} (${config.pass_percentage}%)` : "Not set"}
                </span>
              </div>
            </div>
            {bands.length > 0 && (
              <div className="scoring-preview-bands">
                {bands.map((band, i) => (
                  <span key={band.id ?? i} className="scoring-preview-band-chip">
                    {band.grade || "—"} <small>{band.minimum ?? "0"}–{band.maximum ?? "100"}%</small>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div
            ref={gradeBandsSectionRef}
            className={`grade-bands-box${highlightGradeBands ? " is-highlighted" : ""}${error ? " has-error" : ""}`}
          >
            {error && (
              <div className="grade-bands-error">
                <div className="grade-bands-error-heading">
                  <WarningAmberIcon fontSize="small" aria-hidden="true" />
                  Grade configuration is invalid.
                </div>
                <p className="mb-2">
                  {bands.length
                    ? "Fix the grade bands below so they don't overlap and stay within 0-100, then Save Scoring."
                    : "This feedback form does not use grade bands. Save Scoring to store an empty grade configuration."}
                </p>
                {!readOnly && !bands.length && (
                  <Form.Check
                    type="checkbox"
                    label="No Grade Bands"
                    checked
                    readOnly
                    className="mb-2"
                  />
                )}
                <details>
                  <summary className="small text-muted" style={{ cursor: "pointer" }}>Technical details</summary>
                  <code className="d-block mt-1 small">{error.message}</code>
                </details>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong className="small">Grade Bands (optional)</strong>
              <div className="d-flex align-items-center gap-2">
                {!bands.length && <span className="badge bg-light text-muted border">No Grade Bands</span>}
                {!readOnly && <Button size="sm" variant="outline-primary" onClick={addBand}>+ Add Band</Button>}
              </div>
            </div>
          </div>
          {bands.length > 0 && (
            <Table size="sm" responsive className="mb-2">
              <thead>
                <tr>
                  <th>Minimum %</th>
                  <th>Maximum %</th>
                  <th>Grade</th>
                  <th>Label</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {bands.map((band, index) => (
                  <tr key={band.id ?? index}>
                    <td><Form.Control size="sm" type="number" min={0} max={100} disabled={readOnly} value={band.minimum ?? ""} onChange={(e) => updateBand(index, { minimum: e.target.value })} /></td>
                    <td><Form.Control size="sm" type="number" min={0} max={100} disabled={readOnly} value={band.maximum ?? ""} onChange={(e) => updateBand(index, { maximum: e.target.value })} /></td>
                    <td><Form.Control size="sm" disabled={readOnly} value={band.grade ?? ""} onChange={(e) => updateBand(index, { grade: e.target.value })} placeholder="A+" /></td>
                    <td><Form.Control size="sm" disabled={readOnly} value={band.label ?? ""} onChange={(e) => updateBand(index, { label: e.target.value })} placeholder="Outstanding" /></td>
                    <td>{!readOnly && <Button size="sm" variant="outline-danger" onClick={() => removeBand(index)}>×</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {!bands.length && <div className="text-muted small mb-2">No grade bands configured — valid for a feedback form with no grade/pass-fail requirement. A percentage is still calculated, just without a letter grade/performance band.</div>}
          <Form.Text muted>Bands must not overlap and must stay within 0-100 — validated when you save and again before publishing.</Form.Text>
        </>
      )}
    </div>
  );
});

export default ScoringConfigEditor;
